// RedeemCode.tsx

import React, { useState } from "react";
import { Grid, Card, CardContent, Box, Typography, TextField, Button } from "@mui/material";
import { Ticket, Gift } from "lucide-react";
import { useTranslation } from "react-i18next";
import billingAndRewardsApi from "@/services/billingAndRewards";

interface RedeemCodesProps {
  onFeedback: (feedback: { type: "success" | "error"; message: string } | null) => void;
}

export const RedeemCodes: React.FC<RedeemCodesProps> = ({ onFeedback }) => {
  const { t } = useTranslation();
  const [promoCode, setPromoCode] = useState<string>("");
  const [referralCodeInput, setReferralCodeInput] = useState<string>("");

  const handleApplyPromo = (e: React.FormEvent) => {
    e.preventDefault();
    billingAndRewardsApi.applyPromoCode({ code: promoCode, currentOrderSubtotal: 101 }).then((r) => {
      console.log("r");
      console.log(r);
    });

    if (promoCode.toUpperCase() === "SUMMER20") {
      onFeedback({
        type: "success",
        message: t("components.redeemCode.feedback.promoSuccess", { code: "SUMMER20" }),
      });
    } else {
      onFeedback({
        type: "error",
        message: t("components.redeemCode.feedback.promoError"),
      });
    }
  };

  const handleRedeemReferral = (e: React.FormEvent) => {
    e.preventDefault();
    if (referralCodeInput.trim().length < 5) {
      onFeedback({
        type: "error",
        message: t("components.redeemCode.feedback.referralError"),
      });
    } else {
      onFeedback({
        type: "success",
        message: t("components.redeemCode.feedback.referralSuccess"),
      });
    }
  };

  return (
    <Grid container spacing={3}>
      {/* Promo Discount Code Component */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%", bgcolor: "background.paper" }}
        >
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Ticket color="var(--mui-palette-primary-main)" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
                {t("components.redeemCode.promoTitle")}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              {t("components.redeemCode.promoDescription")}
            </Typography>
            <form onSubmit={handleApplyPromo}>
              <TextField
                fullWidth
                size="small"
                label={t("components.redeemCode.promoLabel")}
                variant="outlined"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                sx={{ mb: 2 }}
              />
              <Button type="submit" variant="contained" color="primary" fullWidth sx={{ borderRadius: 2 }}>
                {t("components.redeemCode.applyPromoButton")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Grid>

      {/* Referral Intake Form Component */}
      <Grid size={{ xs: 12, md: 6 }}>
        <Card
          elevation={0}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%", bgcolor: "background.paper" }}
        >
          <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Gift color="var(--mui-palette-success-main)" />
              <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
                {t("components.redeemCode.referralTitle")}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
              {t("components.redeemCode.referralDescription")}
            </Typography>
            <form onSubmit={handleRedeemReferral}>
              <TextField
                fullWidth
                size="small"
                label={t("components.redeemCode.referralLabel")}
                variant="outlined"
                value={referralCodeInput}
                onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                sx={{ mb: 2 }}
              />
              <Button type="submit" variant="outlined" color="success" fullWidth sx={{ borderRadius: 2 }}>
                {t("components.redeemCode.redeemReferralButton")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
